import { FormattedMessage } from "react-intl";
import styled from "styled-components";
import { TEmployeeOccupiedScheduleItem } from "../../../../../api/employee/apiGetEmployeeOccupiedSchedule";
import { cuideoTheme } from "../../../../../containers/themes/defaultTheme";
import {
  DAYS,
  OccupiedBlockKind,
  getOccupiedPalette,
  Schedule,
  TimeRange,
  formatOccupiedBlockLabel,
} from "../../../../jobsite/components/WeeklyCalendar";
import { StyledLabel } from "../CoordinatorFileSC";

const StyledLegend = styled.div`
  margin-top: 16px;
  margin-bottom: 10px;
  font-family: ${cuideoTheme.typography.fontFamily};
`;

const StyledLegendItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 6px;
  padding: 2px 0 2px 18px;
  font-size: 0.8rem;
  color: ${cuideoTheme.palette.primary.main};
`;

const StyledSwatch = styled.span<{ $kind?: OccupiedBlockKind }>`
  flex-shrink: 0;
  width: 12px;
  height: 12px;
  margin-top: 2px;
  border: 1px solid ${({ $kind }) => getOccupiedPalette($kind).border};
  border-radius: 2px;
  background-image: ${({ $kind }) => getOccupiedPalette($kind).background};
`;

const StyledGroup = styled.div`
  margin-top: 6px;
`;

const StyledGroupTitle = styled.div<{ $kind: OccupiedBlockKind }>`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8rem;
  font-weight: 600;
  color: ${({ $kind }) => getOccupiedPalette($kind).text};
`;

const StyledHint = styled.div`
  padding-left: 18px;
  font-size: 0.75rem;
  font-style: italic;
  color: ${cuideoTheme.palette.grey[600]};
`;

const sameRanges = (a: TimeRange[] = [], b: TimeRange[] = []): boolean =>
  a.length === b.length &&
  a.every((range, idx) => range[0] === b[idx][0] && range[1] === b[idx][1]);

// Resume el horario agrupando los días consecutivos con las mismas franjas,
// p. ej. «lun–vie 00:00-24:00, sáb 00:00-09:00, dom 21:00-24:00».
export const summarizeOccupiedSchedule = (schedule: Schedule): string => {
  const parts: string[] = [];
  const dayLabel = (idx: number) => DAYS[idx].label.toLowerCase();
  let i = 0;
  while (i < DAYS.length) {
    const ranges = schedule[DAYS[i].key] || [];
    if (!ranges.length) {
      i++;
      continue;
    }
    let j = i;
    while (j + 1 < DAYS.length && sameRanges(schedule[DAYS[j + 1].key], ranges)) {
      j++;
    }
    const days = j === i ? dayLabel(i) : `${dayLabel(i)}–${dayLabel(j)}`;
    const hours = ranges.map(([start, end]) => `${start}-${end}`).join(" / ");
    parts.push(`${days} ${hours}`);
    i = j + 1;
  }
  return parts.join(", ");
};

interface IOccupiedBlocksLegendProps {
  blocks: TEmployeeOccupiedScheduleItem[];
}

/**
 * Leyenda de los horarios ocupados por los contratos de la candidata (ECO-1499).
 * Se muestra aunque no haya calendario de «Horario preferido» (internas).
 */
const GROUPS: {
  kind: OccupiedBlockKind;
  title: JSX.Element;
  hint: JSX.Element;
}[] = [
  {
    kind: "occupied",
    title: (
      <FormattedMessage
        id="CoordinatorFile.OccupiedBlocks.OccupiedGroup"
        defaultMessage="Ocupada (contratos seleccionados o activos)"
      />
    ),
    hint: (
      <FormattedMessage
        id="CoordinatorFile.OccupiedBlocks.Hint"
        defaultMessage="Se calcula a partir de sus contratos seleccionados o activos y no se puede editar ni eliminar desde aquí."
      />
    ),
  },
  {
    kind: "inProcess",
    title: (
      <FormattedMessage
        id="CoordinatorFile.OccupiedBlocks.InProcessGroup"
        defaultMessage="En proceso de selección en otros contratos"
      />
    ),
    hint: (
      <FormattedMessage
        id="CoordinatorFile.OccupiedBlocks.InProcessHint"
        defaultMessage="Se calcula a partir de los procesos de selección abiertos en los que participa (aún sin contratar ni descartar). Esas horas todavía no están ocupadas y no se pueden editar ni eliminar desde aquí."
      />
    ),
  },
];

const OccupiedBlocksLegend = ({ blocks }: IOccupiedBlocksLegendProps) => {
  if (!blocks.length) return null;

  return (
    <StyledLegend data-testid="occupied-blocks-legend">
      <StyledLabel>
        <FormattedMessage
          id="CoordinatorFile.OccupiedBlocks.Title"
          defaultMessage="Horario ocupado por contratos"
        />
      </StyledLabel>
      {GROUPS.map(({ kind, title, hint }) => {
        // Sin `kind` (backend antiguo) el bloque se considera ocupado.
        const groupBlocks = blocks.filter(
          (block) => (block.kind ?? "occupied") === kind
        );
        if (!groupBlocks.length) return null;
        return (
          <StyledGroup key={kind} data-testid={`occupied-blocks-group-${kind}`}>
            <StyledGroupTitle $kind={kind}>
              <StyledSwatch $kind={kind} />
              {title}
            </StyledGroupTitle>
            {groupBlocks.map((block) => (
              <StyledLegendItem key={block.contractId}>
                <span>
                  <strong>{formatOccupiedBlockLabel(block)}</strong>
                  {block.estado ? ` (${block.estado})` : ""}
                  {`: ${summarizeOccupiedSchedule(block.schedule)}`}
                </span>
              </StyledLegendItem>
            ))}
            <StyledHint>{hint}</StyledHint>
          </StyledGroup>
        );
      })}
    </StyledLegend>
  );
};

export default OccupiedBlocksLegend;
